import {
  ReceptionProfession,
  ReceptionSnsPlatform,
  ReceptionVisitMotive,
} from 'types/oms';

export const RECEPTION_LANGS = ['en', 'ja', 'zh-CN', 'zh-TW'] as const;

export type ReceptionLang = (typeof RECEPTION_LANGS)[number];

export const RECEPTION_LANG_LABELS: Record<ReceptionLang, string> = {
  en: 'EN',
  ja: '日本語',
  'zh-CN': '简体',
  'zh-TW': '繁體',
};

export const resolveReceptionLang = (value: string | null): ReceptionLang => {
  if (
    value === 'en' ||
    value === 'ja' ||
    value === 'zh-CN' ||
    value === 'zh-TW'
  ) {
    return value;
  }
  return 'en';
};

type OptionLabels<T extends string> = Record<T, string>;

export type ReceptionCopy = {
  back: string;
  backToList: string;
  formTitle: string;
  formSubtitle: string;
  dateLabel: string;
  dateSubLabel: string;
  submissionId: string;
  year: string;
  month: string;
  day: string;
  furigana: string;
  name: string;
  postCode: string;
  address: string;
  tel: string;
  email: string;
  profession: string;
  visitMotive: string;
  sns: string;
  selectOne: string;
  otherPlaceholder: string;
  privacy: string;
  submit: string;
  edit: string;
  print: string;
  submitted: string;
  noSubmission: string;
  notJapanStore: string;
  loadFailed: string;
  nameRequired: string;
  postCodeRequired: string;
  addressRequired: string;
  telRequired: string;
  professionRequired: string;
  professionOtherRequired: string;
  visitMotiveRequired: string;
  visitMotiveOtherRequired: string;
  snsRequired: string;
  professionOptions: OptionLabels<ReceptionProfession>;
  visitMotiveOptions: OptionLabels<ReceptionVisitMotive>;
  snsOptions: OptionLabels<ReceptionSnsPlatform>;
};

const ja: ReceptionCopy = {
  back: '詳細に戻る',
  backToList: '一覧に戻る',
  formTitle: '買取・委託受付表',
  formSubtitle: 'Reception',
  dateLabel: '受付日',
  dateSubLabel: 'Date',
  submissionId: '持ち込みID',
  year: '年',
  month: '月',
  day: '日',
  furigana: 'フリガナ',
  name: 'お名前',
  postCode: '郵便番号',
  address: 'ご住所',
  tel: 'TEL',
  email: 'メールアドレス',
  profession: 'ご職業',
  visitMotive: 'ご来店動機',
  sns: 'SNS',
  selectOne: '下記いずれかにチェックをお願い致します。',
  otherPlaceholder: 'その他の内容',
  privacy:
    '個人情報は、行政指導による聞き取りで、古物業務以外での使用する事はございません。',
  submit: '送信する',
  edit: '修正する',
  print: '印刷する',
  submitted: '送信しました',
  noSubmission: 'この注文には取引番号がありません',
  notJapanStore: 'この店舗では受付表を利用できません',
  loadFailed: '受付表を読み込めませんでした',
  nameRequired: 'お名前を入力してください',
  postCodeRequired: '郵便番号を入力してください',
  addressRequired: 'ご住所を入力してください',
  telRequired: '電話番号を入力してください',
  professionRequired: 'ご職業を選択してください',
  professionOtherRequired: 'ご職業の内容を入力してください',
  visitMotiveRequired: 'ご来店動機を選択してください',
  visitMotiveOtherRequired: 'ご来店動機の内容を入力してください',
  snsRequired: 'SNSを選択してください',
  professionOptions: {
    company_employee: '会社員',
    company_owner: '会社経営',
    company_officer: '会社役員',
    self_employed: '自営業',
    civil_servant: '公務員',
    part_time: 'パート・アルバイト',
    student: '学生',
    homemaker: '専業主婦',
    other: 'その他',
  },
  visitMotiveOptions: {
    official_site: '弊社HP',
    sns: 'SNS',
    referral: 'ご紹介',
    passing_by: '通りすがり',
    google: 'Google',
    signboard: '看板',
    repeater: 'リピーター様',
    other: 'その他',
  },
  snsOptions: {
    instagram: 'Instagram',
    wechat: 'WeChat',
    whatsapp: 'WhatsApp',
    line: 'LINE',
    red: '小紅書（RED）',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    ai_search: 'AI検索',
  },
};

const en: ReceptionCopy = {
  back: 'Back to details',
  backToList: 'Back to list',
  formTitle: 'Purchase / Consignment Reception',
  formSubtitle: 'Reception',
  dateLabel: 'Date',
  dateSubLabel: '',
  submissionId: 'Submission ID',
  year: '/',
  month: '/',
  day: '',
  furigana: 'Furigana',
  name: 'Name',
  postCode: 'Postal code',
  address: 'Address',
  tel: 'TEL',
  email: 'Email',
  profession: 'Profession',
  visitMotive: 'How did you find us',
  sns: 'SNS',
  selectOne: 'Please select one.',
  otherPlaceholder: 'Please specify',
  privacy:
    'Personal information is collected only for administrative inquiries and will not be used for purposes other than the antique business.',
  submit: 'Submit',
  edit: 'Edit',
  print: 'Print',
  submitted: 'Submitted',
  noSubmission: 'This order has no transaction number',
  notJapanStore: 'This store does not use the reception form',
  loadFailed: 'Could not load the reception form',
  nameRequired: 'Please enter your name',
  postCodeRequired: 'Please enter your postal code',
  addressRequired: 'Please enter your address',
  telRequired: 'Please enter your phone number',
  professionRequired: 'Please select your profession',
  professionOtherRequired: 'Please specify your profession',
  visitMotiveRequired: 'Please select how you found us',
  visitMotiveOtherRequired: 'Please specify how you found us',
  snsRequired: 'Please select an SNS',
  professionOptions: {
    company_employee: 'Company employee',
    company_owner: 'Company owner',
    company_officer: 'Company officer',
    self_employed: 'Self-employed',
    civil_servant: 'Civil servant',
    part_time: 'Part-time',
    student: 'Student',
    homemaker: 'Homemaker',
    other: 'Other',
  },
  visitMotiveOptions: {
    official_site: 'Our website',
    sns: 'SNS',
    referral: 'Referral',
    passing_by: 'Passing by',
    google: 'Google',
    signboard: 'Signboard',
    repeater: 'Repeat customer',
    other: 'Other',
  },
  snsOptions: {
    instagram: 'Instagram',
    wechat: 'WeChat',
    whatsapp: 'WhatsApp',
    line: 'LINE',
    red: 'Xiaohongshu (RED)',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    ai_search: 'AI search',
  },
};

const zhCN: ReceptionCopy = {
  back: '返回详情',
  backToList: '返回列表',
  formTitle: '买取・委托受付表',
  formSubtitle: 'Reception',
  dateLabel: '受付日',
  dateSubLabel: 'Date',
  submissionId: '登记ID',
  year: '年',
  month: '月',
  day: '日',
  furigana: '假名',
  name: '姓名',
  postCode: '邮编',
  address: '住址',
  tel: '电话',
  email: '邮箱',
  profession: '职业',
  visitMotive: '来店动机',
  sns: 'SNS',
  selectOne: '请选择一项。',
  otherPlaceholder: '请填写具体内容',
  privacy: '个人信息仅用于行政指导问询，不会用于古物业务以外的用途。',
  submit: '提交',
  edit: '修改',
  print: '打印',
  submitted: '已提交',
  noSubmission: '这张订单没有交易编号',
  notJapanStore: '当前门店不使用受付表',
  loadFailed: '受付表加载失败',
  nameRequired: '请填写姓名',
  postCodeRequired: '请填写邮编',
  addressRequired: '请填写住址',
  telRequired: '请填写电话',
  professionRequired: '请选择职业',
  professionOtherRequired: '请填写职业',
  visitMotiveRequired: '请选择来店动机',
  visitMotiveOtherRequired: '请填写来店动机',
  snsRequired: '请选择 SNS',
  professionOptions: {
    company_employee: '公司职员',
    company_owner: '公司经营',
    company_officer: '公司董事',
    self_employed: '自营',
    civil_servant: '公务员',
    part_time: '兼职',
    student: '学生',
    homemaker: '全职主妇',
    other: '其他',
  },
  visitMotiveOptions: {
    official_site: '官网',
    sns: 'SNS',
    referral: '介绍',
    passing_by: '路过',
    google: 'Google',
    signboard: '看板',
    repeater: '回头客',
    other: '其他',
  },
  snsOptions: {
    instagram: 'Instagram',
    wechat: 'WeChat',
    whatsapp: 'WhatsApp',
    line: 'LINE',
    red: '小红书（RED）',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    ai_search: 'AI 搜索',
  },
};

const zhTW: ReceptionCopy = {
  back: '返回詳情',
  backToList: '返回列表',
  formTitle: '買取・委託受付表',
  formSubtitle: 'Reception',
  dateLabel: '受付日',
  dateSubLabel: 'Date',
  submissionId: '登記 ID',
  year: '年',
  month: '月',
  day: '日',
  furigana: '假名',
  name: '姓名',
  postCode: '郵遞區號',
  address: '住址',
  tel: '電話',
  email: '郵箱',
  profession: '職業',
  visitMotive: '來店動機',
  sns: 'SNS',
  selectOne: '請選擇一項。',
  otherPlaceholder: '請填寫具體內容',
  privacy: '個人資訊僅用於行政指導問詢，不會用於古物業務以外的用途。',
  submit: '提交',
  edit: '修改',
  print: '列印',
  submitted: '已提交',
  noSubmission: '這張訂單沒有交易編號',
  notJapanStore: '目前門店不使用受付表',
  loadFailed: '受付表載入失敗',
  nameRequired: '請填寫姓名',
  postCodeRequired: '請填寫郵遞區號',
  addressRequired: '請填寫住址',
  telRequired: '請填寫電話',
  professionRequired: '請選擇職業',
  professionOtherRequired: '請填寫職業',
  visitMotiveRequired: '請選擇來店動機',
  visitMotiveOtherRequired: '請填寫來店動機',
  snsRequired: '請選擇 SNS',
  professionOptions: {
    company_employee: '公司職員',
    company_owner: '公司經營',
    company_officer: '公司董事',
    self_employed: '自營',
    civil_servant: '公務員',
    part_time: '兼職',
    student: '學生',
    homemaker: '全職主婦',
    other: '其他',
  },
  visitMotiveOptions: {
    official_site: '官網',
    sns: 'SNS',
    referral: '介紹',
    passing_by: '路過',
    google: 'Google',
    signboard: '看板',
    repeater: '回頭客',
    other: '其他',
  },
  snsOptions: {
    instagram: 'Instagram',
    wechat: 'WeChat',
    whatsapp: 'WhatsApp',
    line: 'LINE',
    red: '小紅書（RED）',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    ai_search: 'AI 搜尋',
  },
};

export const RECEPTION_COPY: Record<ReceptionLang, ReceptionCopy> = {
  en,
  ja,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
};
